import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact, {
  type BugsnagErrorBoundary as BugsnagReactErrorBoundary,
} from "@bugsnag/plugin-react";
import React, { type ReactNode, useEffect, useState } from "react";
import {
  bugsnagRedactedKeys,
} from "@/lib/bugsnag-config";

type BugsnagBrowserConfig = {
  apiKey: string | null;
  appVersion?: string;
  enabledReleaseStages: string[];
  releaseStage: string;
  siteCode: string;
};

let errorBoundary: BugsnagReactErrorBoundary | null = null;
let bugsnagStartPromise: Promise<BugsnagReactErrorBoundary | null> | null = null;

type BreadcrumbWindow = Window & {
  __rlBugsnagLeaveBreadcrumb?: (
    name: string,
    metadata?: Record<string, unknown>,
  ) => void;
};

async function fetchBrowserConfig() {
  const response = await fetch("/api/bugsnag-config", {
    cache: "no-store",
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error(`Bugsnag config request failed with ${response.status}.`);
  }

  return (await response.json()) as BugsnagBrowserConfig;
}

async function startBrowserBugsnag() {
  if (typeof window === "undefined") {
    return null;
  }

  if (errorBoundary) {
    return errorBoundary;
  }

  const config = await fetchBrowserConfig();
  const apiKey = config.apiKey?.trim();

  if (!apiKey) {
    return null;
  }

  if (!Bugsnag.isStarted()) {
    Bugsnag.start({
      apiKey,
      appType: "nextjs-browser",
      appVersion: config.appVersion,
      releaseStage: config.releaseStage,
      enabledReleaseStages: config.enabledReleaseStages,
      redactedKeys: bugsnagRedactedKeys,
      collectUserIp: false,
      plugins: [new BugsnagPluginReact(React)],
      metadata: {
        rocklife: {
          app: "storefront-web",
          runtime: "browser",
        },
      },
      onError(event) {
        if (shouldSuppressBrowserError(event)) {
          return false;
        }
        const snipcartDisposition = classifySnipcartSystemError(event);
        if (snipcartDisposition === "drop") {
          return false;
        }
        if (snipcartDisposition === "downgrade") {
          // A mobile interaction race inside Snipcart (e.g. a double-tapped
          // remove button -> 404 -> "invalid payload from server"), not a
          // crash. Keep it visible for monitoring but de-emphasise it so it
          // doesn't pollute the error list; a genuine backend-induced spike
          // would still be observable here and server-side.
          event.severity = "info";
        }
        event.addMetadata("rocklife", {
          siteCode:
            document.documentElement.getAttribute("data-storefront-site") ??
            config.siteCode,
        });
        return true;
      },
    });
  }

  (window as BreadcrumbWindow).__rlBugsnagLeaveBreadcrumb = (
    name,
    metadata = {},
  ) => {
    try {
      Bugsnag.leaveBreadcrumb(name, sanitizeBreadcrumbMetadata(metadata), "manual");
    } catch {}
  };

  errorBoundary = Bugsnag.getPlugin("react")?.createErrorBoundary(React) ?? null;
  return errorBoundary;
}

function shouldSuppressBrowserError(event: unknown) {
  const text = extractEventText(event).toLowerCase();
  const hasSnipcartStack = text.includes("cdn.snipcart.com/themes/v");

  return (
    text.includes("bugsnag browser smoke") ||
    text.includes("bugsnag smoke") ||
    text.includes('navigation cancelled from "/" to "/checkout" with a new navigation.') ||
    (text.includes("navigation abort") && text.includes("snipcart")) ||
    (text.includes("abort fetching component for route") && text.includes("checkout")) ||
    (hasSnipcartStack && text.includes("attempted to assign to readonly property.")) ||
    (hasSnipcartStack && text.includes("cannot create property '__na' on string"))
  );
}

// Snipcart rejects some internal promises with a non-Error system object
// ({ kind, code, message, technicalReason }) and never catches them, so they
// reach window.onunhandledrejection and Bugsnag reports them as InvalidError.
// These are client-side races against Snipcart's own API/iframe, not our code:
//   * "Payment iframe was removed before completing initialization" — the
//     customer tapped "Edit" while the payment iframe was initialising. Pure UX
//     race; drop it.
//   * anything else (e.g. a double-tapped remove button -> 404 -> "Received
//     invalid payload from server") — also a race, but the message is generic
//     enough that a real backend-induced payload error could share it, so
//     downgrade rather than drop and keep monitoring.
function classifySnipcartSystemError(event: unknown): "drop" | "downgrade" | null {
  const systemError = extractSnipcartSystemError(event);
  if (!systemError) {
    return null;
  }
  if (
    systemError.technicalReason?.toLowerCase().includes("payment iframe was removed")
  ) {
    return "drop";
  }
  return "downgrade";
}

function extractSnipcartSystemError(
  event: unknown,
): { code?: string; technicalReason?: string } | null {
  const candidates: Array<Record<string, unknown>> = [];
  try {
    const original = (event as { originalError?: unknown }).originalError;
    if (original && typeof original === "object") {
      candidates.push(original as Record<string, unknown>);
    }
  } catch {}
  try {
    const getMetadata = (
      event as { getMetadata?: (section: string, key?: string) => unknown }
    ).getMetadata;
    if (typeof getMetadata === "function") {
      const meta = getMetadata.call(
        event,
        "unhandledrejection handler",
        "non-error parameter",
      );
      if (meta && typeof meta === "object") {
        candidates.push(meta as Record<string, unknown>);
      }
    }
  } catch {}
  for (const candidate of candidates) {
    const code = typeof candidate.code === "string" ? candidate.code : undefined;
    const message =
      typeof candidate.message === "string" ? candidate.message : undefined;
    const technicalReason =
      typeof candidate.technicalReason === "string"
        ? candidate.technicalReason
        : undefined;
    if (
      code === "snipcart_error_operation_failed" ||
      message === "snipcart_error_operation_failed"
    ) {
      return { code, technicalReason };
    }
  }
  return null;
}

function extractEventText(event: unknown) {
  try {
    const candidate = event as {
      errors?: Array<{ errorMessage?: string; stacktrace?: unknown }>;
      originalError?: { message?: string; stack?: string };
    };
    return [
      candidate.errors?.map((error) => error.errorMessage).join(" "),
      JSON.stringify(candidate.errors?.map((error) => error.stacktrace) ?? []),
      candidate.originalError?.message,
      candidate.originalError?.stack,
    ]
      .filter(Boolean)
      .join(" ");
  } catch {
    return "";
  }
}

function sanitizeBreadcrumbMetadata(metadata: Record<string, unknown>) {
  const safe: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (/authorization|cookie|password|token|secret|email|phone|address|payment/i.test(key)) {
      safe[key] = "[redacted]";
      continue;
    }
    safe[key] =
      typeof value === "string" && value.length > 160
        ? `${value.slice(0, 157)}...`
        : value;
  }
  return safe;
}

function getBugsnagErrorBoundary() {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }

  bugsnagStartPromise ??= startBrowserBugsnag().catch((error) => {
    console.warn("[bugsnag] Failed to initialize browser Bugsnag.", error);
    return null;
  });

  return bugsnagStartPromise;
}

if (typeof window !== "undefined") {
  void getBugsnagErrorBoundary();
}

function StorefrontErrorFallback({ clearError }: { clearError: () => void }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-5 py-10 text-center">
      <section className="max-w-md">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-neutral-500">
          Something went wrong
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-neutral-950">
          Please refresh and try again.
        </h1>
        <button
          className="mt-6 rounded-lg bg-neutral-950 px-5 py-3 text-sm font-semibold text-white"
          type="button"
          onClick={clearError}
        >
          Try again
        </button>
      </section>
    </main>
  );
}

export function BugsnagErrorBoundary({ children }: { children: ReactNode }) {
  const [ErrorBoundary, setErrorBoundary] =
    useState<BugsnagReactErrorBoundary | null>(() => errorBoundary);

  useEffect(() => {
    let isMounted = true;

    void getBugsnagErrorBoundary().then((boundary) => {
      if (isMounted) {
        setErrorBoundary(() => boundary);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!ErrorBoundary) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary FallbackComponent={StorefrontErrorFallback}>
      {children}
    </ErrorBoundary>
  );
}
